import React, { useState } from 'react';
import { Visibility as PreviewIcon } from '@mui/icons-material';
import { findDraftMatches, type DraftPreviewItem } from './draftSearchMatcher';

interface DraftKnowledgeSearchProps {
  items: DraftPreviewItem[];
}

/** One whole-draft confidence check. This is intentionally lexical preview
 * matching over the selected unpublished answers, not the live search index. */
const DraftKnowledgeSearchView: React.FC<DraftKnowledgeSearchProps> = ({ items }) => {
  const [query, setQuery] = useState('');
  const [searched, setSearched] = useState(false);
  const matches = searched ? findDraftMatches(query, items) : [];

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (query.trim().length >= 2) setSearched(true);
  };

  // There is nothing meaningful to test until at least one drafted answer is
  // selected. Hiding the whole tool avoids presenting an empty, button-like
  // section whose input appears to be missing.
  if (items.length === 0) return null;

  return (
    <section aria-labelledby="draft-search-heading" className="rounded-xl border border-hairline bg-surface-subtle p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-ink">
        <PreviewIcon style={{ fontSize: '1rem' }} className="text-ink-muted" />
        <h2 id="draft-search-heading">Test the whole draft</h2>
      </div>
      <form onSubmit={handleSubmit} className="mt-3 flex gap-2">
            <input
              aria-label="Buyer question to test against the draft"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Try a buyer question"
              className="min-w-0 flex-1 rounded-lg border border-ink-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-signature"
            />
            <button type="submit" className="shrink-0 rounded-lg border border-signature bg-surface px-3 py-2 text-sm font-semibold text-signature hover:bg-signature-tint">
              Test
            </button>
      </form>
      {searched && (
        <div className="mt-3 space-y-2">
          {matches.length === 0 ? (
            <p className="text-xs text-ink-secondary">No close match. This is probably a gap worth adding through the guide.</p>
          ) : matches.map((result) => (
            <div key={result.tempId} className="rounded-lg border border-hairline bg-surface p-3">
              <span className="rounded-full border border-ink-border bg-surface-sunken text-ink-secondary px-2 py-0.5 text-[11px] font-medium">
                Draft {result.type === 'faq' ? 'FAQ' : 'objection'}, not live
              </span>
              <p className="mt-2 text-sm font-medium text-ink">{result.question}</p>
              <p className="mt-1 line-clamp-2 text-sm text-ink-secondary">{result.answer}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default DraftKnowledgeSearchView;
